import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "ui/card";
import { Button } from "ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import { Badge } from "ui/badge";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { getQarzanTypes, QarzanTypeApi } from "services/employeeService";

export interface Loan {
  id: string;
  type: string;
  amount: number;
  issuedDate: string;
  dueDate: string;
  remaining: number;
  status: "Active" | "Closed" | "Inactive";
}

interface Props {
  loans: Loan[];
  isViewMode?: boolean;
  setLoans: React.Dispatch<React.SetStateAction<Loan[]>>;
  saveQarzan: (loan: Loan) => Promise<void>;
}

export default function LoansTab({ loans, isViewMode = false, setLoans, saveQarzan }: Props) {
  const [showLoanDialog, setShowLoanDialog] = useState(false);
  const [loanErrors, setLoanErrors] = useState<Record<string, string>>({});
  const [qarzanTypes, setQarzanTypes] = useState<QarzanTypeApi[]>([]);

  const [newLoan, setNewLoan] = useState<Omit<Loan, "id">>({
    type: "",
    amount: 0,
    issuedDate: "",
    dueDate: "",
    remaining: 0,
    status: "Active",
  });

  useEffect(() => {
    const loadQarzanTypes = async () => {
      try {
        const res = await getQarzanTypes();

        setQarzanTypes(res.filter((item) => item.isActive));
      } catch (error) {
        console.error(error);

        toast.error("Failed to load Qarzan types");
      }
    };

    loadQarzanTypes();
  }, []);

  const addLoan = async () => {
    const errors: Record<string, string> = {};

    if (!newLoan.type.trim()) {
      errors.type = "Loan Type is required";
    }

    if (newLoan.amount <= 0) {
      errors.amount = "Amount must be greater than 0";
    }

    if (!newLoan.issuedDate) {
      errors.issuedDate = "Issued Date is required";
    }

    if (!newLoan.dueDate) {
      errors.dueDate = "Due Date is required";
    }

    setLoanErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast.error("Please fill all required fields");

      return;
    }

    await saveQarzan({
      id: "",

      ...newLoan,
    });

    setNewLoan({
      type: "",
      amount: 0,
      issuedDate: "",
      dueDate: "",
      remaining: 0,
      status: "Active",
    });

    setLoanErrors({});

    setShowLoanDialog(false);

    toast.success("Loan added successfully");
  };

  return (
    <>
      <Card className="bg-white" style={{ gap: "10px" }}>
        <CardHeader>
          <CardTitle>Qarz-e-Hasana</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                  <TableHead style={{ color: "#5B9BD5" }} className="text-xs">
                    Type<span className="text-red-500 ml-1">*</span>
                  </TableHead>
                  <TableHead style={{ color: "#5B9BD5" }} className="text-xs">
                    Amount<span className="text-red-500 ml-1">*</span>
                  </TableHead>
                  <TableHead style={{ color: "#5B9BD5" }} className="text-xs">
                    Issued Date<span className="text-red-500 ml-1">*</span>
                  </TableHead>
                  <TableHead style={{ color: "#5B9BD5" }} className="text-xs">
                    Due Date<span className="text-red-500 ml-1">*</span>
                  </TableHead>
                  <TableHead style={{ color: "#5B9BD5" }} className="text-xs">
                    Remaining
                  </TableHead>
                  <TableHead style={{ color: "#5B9BD5" }} className="text-xs">
                    Status
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loans.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-6 text-gray-500 text-sm"
                    >
                      No loans found for this employee
                    </TableCell>
                  </TableRow>
                ) : (
                  loans.map((loan) => (
                    <TableRow key={loan.id}>
                      <TableCell className="font-medium">{loan.type}</TableCell>
                      <TableCell>{loan.amount}</TableCell>
                      <TableCell>{loan.issuedDate}</TableCell>
                      <TableCell>{loan.dueDate}</TableCell>
                      <TableCell>{loan.remaining}</TableCell>
                      <TableCell>
                        <Badge
                          className={
                            loan.status === "Active"
                              ? "bg-green-100 text-green-700 border border-green-300"
                              : "bg-gray-100 text-gray-700 border border-gray-300"
                          }
                        >
                          {loan.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {!isViewMode && (
          <Button
            className="mt-4"
            variant="outline"
            size="sm"
            onClick={() => setShowLoanDialog(true)}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Qarzan
          </Button>
          )}
        </CardContent>
      </Card>

      {/* ADD LOAN DIALOG */}
      {showLoanDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h3 className="text-lg font-bold" style={{ color: "#5B9BD5" }}>
                Add Qarzan
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowLoanDialog(false)}
              >
                ✕
              </Button>
            </div>
            <div className="p-4 space-y-4">
              <div className="space-y-2">
                <div className="space-y-2">
                  <SelectFieldset label="Qarzan Type" required>
                    <Select
                      value={newLoan.type}
                      onValueChange={(value) => {
                        setNewLoan({
                          ...newLoan,
                          type: value,
                        });

                        setLoanErrors({
                          ...loanErrors,
                          type: "",
                        });
                      }}
                    >
                      <SelectTriggerUI
                        id="loanType"
                        className={
                          loanErrors.type ? "border-red-500 border-1" : ""
                        }
                      >
                        <SelectValue placeholder="Select Qarzan Type" />
                      </SelectTriggerUI>

                      <SelectContent>
                        {qarzanTypes.map((type) => (
                          <SelectItem key={type.id} value={type.qarzanTypeName}>
                            {type.qarzanTypeName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </SelectFieldset>

                  {loanErrors.type && (
                    <p className="text-red-500 text-xs font-medium mt-1">
                      {loanErrors.type}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Amount"
                  required
                  type="number"
                  id="amount"
                  value={newLoan.amount}
                  onChange={(e) => {
                    setNewLoan({
                      ...newLoan,
                      amount: Number(e.target.value),
                    });
                    setLoanErrors({ ...loanErrors, amount: "" });
                  }}
                  placeholder="0"
                  className={loanErrors.amount ? "border-red-500 border-1" : ""}
                />
                {loanErrors.amount && (
                  <p className="text-red-500 text-xs font-medium mt-1">
                    {loanErrors.amount}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Issued Date"
                  required
                  type="date"
                  id="issuedDate"
                  value={newLoan.issuedDate}
                  onChange={(e) => {
                    setNewLoan({
                      ...newLoan,
                      issuedDate: e.target.value,
                    });
                    setLoanErrors({ ...loanErrors, issuedDate: "" });
                  }}
                  className={
                    loanErrors.issuedDate ? "border-red-500 border-1" : ""
                  }
                />
                {loanErrors.issuedDate && (
                  <p className="text-red-500 text-xs font-medium mt-1">
                    {loanErrors.issuedDate}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Due Date"
                  required
                  type="date"
                  id="dueDate"
                  value={newLoan.dueDate}
                  onChange={(e) => {
                    setNewLoan({
                      ...newLoan,
                      dueDate: e.target.value,
                    });
                    setLoanErrors({ ...loanErrors, dueDate: "" });
                  }}
                  className={
                    loanErrors.dueDate ? "border-red-500 border-1" : ""
                  }
                />
                {loanErrors.dueDate && (
                  <p className="text-red-500 text-xs font-medium mt-1">
                    {loanErrors.dueDate}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Remaining"
                  type="number"
                  id="remaining"
                  value={newLoan.remaining}
                  onChange={(e) =>
                    setNewLoan({
                      ...newLoan,
                      remaining: Number(e.target.value),
                    })
                  }
                  placeholder="0"
                />
              </div>

              <div className="space-y-2">
                <SelectFieldset label="Status" required>
                  <Select
                    value={newLoan.status}
                    onValueChange={(value) => {
                      setNewLoan({
                        ...newLoan,
                        status: value as "Active" | "Closed" | "Inactive",
                      });
                    }}
                  >
                    <SelectTriggerUI id="status">
                      <SelectValue placeholder="Select status" />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Closed">Closed</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t p-4 flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowLoanDialog(false)}
              >
                Cancel
              </Button>
              <Button
                style={{ background: "#5B9BD5" }}
                className="text-white"
                size="sm"
                onClick={addLoan}
              >
                Add Qarzan
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
